// Which tune loops behind the game. Either one track picked by hand, or "auto",
// which plays them one after another and rolls back round to the first.
//
// Like volumePreference, this file is kept free of any audio-engine imports so
// audioManager can depend on it and not the other way round: a change is written
// to localStorage and announced on window, and audioManager picks it up and
// switches the tune that is already playing.
import mainTuneAudioSrc from "@/audio/main-tune.mp3"
import mainTune2AudioSrc from "@/audio/main-tune2.mp3"
import mainTune3AudioSrc from "@/audio/main-tune3.mp3"
import mainTune4AudioSrc from "@/audio/main-tune4.mp3"

export interface IMusicTrack {
  id: string
  name: string
  src: string
}

// Order matters: it is the order auto mode plays them in.
export const musicTracks: IMusicTrack[] = [
  { id: "main-tune", name: "Main Theme", src: mainTuneAudioSrc },
  { id: "main-tune2", name: "Track 2", src: mainTune2AudioSrc },
  { id: "main-tune3", name: "Track 3", src: mainTune3AudioSrc },
  { id: "main-tune4", name: "Track 4", src: mainTune4AudioSrc },
]

export const AUTO_MUSIC_TRACK = "auto"

/** A track id, or `auto` for "play them one by one". */
export type MusicTrackSelection = string

const MUSIC_TRACK_KEY = "musicTrack"
export const MUSIC_TRACK_CHANGED_EVENT = "music-track-changed"

// New players get the rotation rather than the same tune every game.
export const DEFAULT_MUSIC_TRACK: MusicTrackSelection = AUTO_MUSIC_TRACK

export const getMusicTrackSelection = (): MusicTrackSelection => {
  try {
    const stored = localStorage.getItem(MUSIC_TRACK_KEY)
    if (!stored) return DEFAULT_MUSIC_TRACK
    if (stored === AUTO_MUSIC_TRACK) return AUTO_MUSIC_TRACK
    // A track that has since been renamed or removed falls back to the
    // rotation rather than to silence.
    return musicTracks.some((track) => track.id === stored)
      ? stored
      : DEFAULT_MUSIC_TRACK
  } catch {
    // Private-mode / storage-disabled browsers still get music, just not the
    // memory of which one.
    return DEFAULT_MUSIC_TRACK
  }
}

export const setMusicTrackSelection = (
  selection: MusicTrackSelection
): MusicTrackSelection => {
  try {
    localStorage.setItem(MUSIC_TRACK_KEY, selection)
  } catch {}
  window.dispatchEvent(
    new CustomEvent(MUSIC_TRACK_CHANGED_EVENT, { detail: selection })
  )
  return selection
}

export const getMusicTrackSrc = (id: MusicTrackSelection): string | undefined =>
  musicTracks.find((track) => track.id === id)?.src

/** The tune auto mode moves on to when `src` finishes; wraps around at the end. */
export const getNextMusicTrackSrc = (src: string | null): string => {
  const index = musicTracks.findIndex((track) => track.src === src)
  return musicTracks[(index + 1) % musicTracks.length].src
}
